export type NewImpromptuLanguageCode = "zh" | "hi" | "pl" | "ru" | "sv";

/**
 * Authored translations of the 32 prompt intents in
 * `extended-prompt-library.ts`, in the same order as its seed list. Keeping the
 * complete local set here means changing language never depends on Gemini.
 */
export const NEW_LANGUAGE_PROMPT_TEXTS: Readonly<
  Record<NewImpromptuLanguageCode, ReadonlyArray<string>>
> = {
  zh: [
    "每周是否应该有一天不安排会议？请说明你的看法。",
    "市中心是否应该减少汽车？请说明谁会受益，以及什么可能会变得更不方便。",
    "讲述一次你从小错误中学到有用经验的经历。",
    "讲述一次陌生人改变了你一天的经历。",
    "介绍一个常被访客误解的文化习惯。",
    "说明怎样让到家里做客的人感到受欢迎。",
    "说服一位朋友尝试你喜欢的一项活动。",
    "说服你所在的城市增加安静的公共空间。",
    "什么习惯改善了你的日常生活？为什么它对你有效？",
    "哪一个小决定带来的影响比你预想的更大？",
    "和别人一起吃饭时，手机是否应该离开餐桌？为什么？",
    "无现金社会更方便还是风险更大？请给出支持你观点的最有力理由。",
    "讲述一个在最后一刻发生变化的计划。",
    "讲述你丢失某样东西的经历，以及事情最后是怎样解决的。",
    "向完全没有经验的人说明怎样开始一项你熟悉的爱好。",
    "向一个考虑搬来的人介绍你住的社区。",
    "说服别人选择本地小店，而不是大型连锁店。",
    "说服人们修理某一种物品，而不是直接更换。",
    "哪一条建议你至今还会想起？为什么它让你印象深刻？",
    "哪一件普通物品对你的意义远远超过它的价格？",
    "描述你通常的早晨，并说说你最想改变哪一部分。",
    "描述一道让你感到安心的食物，并说明它为什么特别。",
    "描述你平时的一段路程，以及途中你通常会注意到什么。",
    "描述你最喜欢的一个房间，以及它让你喜欢的三个细节。",
    "如果收到你不同意的反馈，你会怎样回应？",
    "当两项重要任务同时需要处理时，你会怎样决定先做什么？",
    "向完全不了解你工作或学习的人介绍你的角色。",
    "如果一个重要期限无法按时完成，你会怎样告诉相关的人？",
    "想象每天多出一个小时。你会怎样使用它？生活会有什么变化？",
    "如果可以和某个人交换一周的日常生活，你会选择谁？为什么？",
    "想象一个没有手机和网络的周末。什么会更轻松，什么会更困难？",
    "想象你明天可以开一家小店。它会提供什么？谁会来？",
  ],
  hi: [
    "क्या हर सप्ताह एक दिन बिना मीटिंग के होना चाहिए? अपना विचार समझाइए।",
    "क्या शहर के केंद्रों में गाड़ियाँ कम होनी चाहिए? बताइए कि इससे किसे लाभ होगा और क्या कठिन हो सकता है।",
    "एक छोटी गलती की कहानी सुनाइए जिससे आपने कोई उपयोगी बात सीखी।",
    "उस समय के बारे में बताइए जब किसी अनजान व्यक्ति ने आपका दिन बदल दिया।",
    "अपनी संस्कृति की कोई ऐसी बात समझाइए जिसे आने वाले लोग अक्सर गलत समझते हैं।",
    "समझाइए कि घर आए मेहमान को सहज और स्वागतयोग्य कैसे महसूस कराया जा सकता है।",
    "किसी मित्र को अपनी पसंद की कोई गतिविधि आज़माने के लिए मनाइए।",
    "अपने शहर को अधिक शांत सार्वजनिक स्थान बनाने के लिए मनाइए।",
    "किस आदत ने आपके रोज़मर्रा के जीवन को बेहतर बनाया और वह आपके लिए क्यों काम करती है?",
    "किस छोटे निर्णय का असर आपकी उम्मीद से बड़ा निकला?",
    "दूसरों के साथ खाना खाते समय क्या फ़ोन मेज़ से दूर रहने चाहिए? क्यों या क्यों नहीं?",
    "नकद रहित समाज सुविधाजनक है या जोखिम भरा? अपने विचार के पक्ष में सबसे मज़बूत तर्क दीजिए।",
    "किसी ऐसी योजना की कहानी सुनाइए जो आख़िरी समय में बदल गई।",
    "किसी खोई हुई वस्तु और उस स्थिति के अंत की कहानी सुनाइए।",
    "समझाइए कि कोई बिल्कुल नया व्यक्ति आपके जाने-पहचाने शौक की शुरुआत कैसे कर सकता है।",
    "अपने मोहल्ले का वर्णन किसी ऐसे व्यक्ति के लिए कीजिए जो वहाँ रहने के बारे में सोच रहा है।",
    "किसी को बड़ी दुकान की जगह स्थानीय दुकान चुनने के लिए मनाइए।",
    "लोगों को किसी वस्तु को बदलने के बजाय उसकी मरम्मत कराने के लिए मनाइए।",
    "आपको आज भी कौन-सी सलाह याद आती है और वह आपके मन में क्यों रही?",
    "कौन-सी साधारण वस्तु आपके लिए उसकी कीमत से कहीं अधिक मायने रखती है?",
    "अपनी सामान्य सुबह का वर्णन कीजिए और बताइए कि आप उसका कौन-सा हिस्सा सबसे अधिक बदलना चाहेंगे।",
    "ऐसे भोजन का वर्णन कीजिए जो आपको सुकून देता है और बताइए कि वह खास क्यों है।",
    "अपनी रोज़ की किसी यात्रा का वर्णन कीजिए और बताइए कि रास्ते में आप आम तौर पर क्या देखते हैं।",
    "अपने पसंदीदा कमरे और उसकी तीन खास बातों का वर्णन कीजिए।",
    "आप ऐसी प्रतिक्रिया का जवाब कैसे देंगे जिससे आप सहमत नहीं हैं?",
    "जब दो ज़रूरी काम एक साथ करने हों, तो आप पहले किसे करेंगे और कैसे तय करेंगे?",
    "अपने काम या पढ़ाई में अपनी भूमिका ऐसे व्यक्ति को समझाइए जो उसके बारे में कुछ नहीं जानता।",
    "आप किसी को कैसे बताएँगे कि एक महत्वपूर्ण समय-सीमा पूरी नहीं हो पाएगी?",
    "कल्पना कीजिए कि हर दिन एक घंटा अधिक हो। आप उसका क्या करेंगे और क्या बदलेगा?",
    "अगर आप एक सप्ताह के लिए किसी के साथ रोज़मर्रा की ज़िंदगी बदल सकें, तो किसे चुनेंगे और क्यों?",
    "बिना फ़ोन और इंटरनेट वाले सप्ताहांत की कल्पना कीजिए। क्या आसान होगा और क्या कठिन?",
    "कल्पना कीजिए कि आप कल एक छोटा व्यवसाय शुरू कर सकते हैं। वह क्या देगा और वहाँ कौन आएगा?",
  ],
  pl: [
    "Czy każdy tydzień powinien mieć jeden dzień bez spotkań? Uzasadnij swoją opinię.",
    "Czy w centrach miast powinno być mniej samochodów? Wyjaśnij, kto by na tym zyskał i co mogłoby stać się trudniejsze.",
    "Opowiedz o małym błędzie, z którego udało ci się wyciągnąć przydatną lekcję.",
    "Opowiedz o sytuacji, w której nieznajoma osoba zmieniła przebieg twojego dnia.",
    "Wyjaśnij jeden element swojej kultury, który odwiedzający często źle rozumieją.",
    "Wyjaśnij, jak sprawić, by gość poczuł się mile widziany w twoim domu.",
    "Przekonaj znajomą osobę do spróbowania aktywności, którą lubisz.",
    "Przekonaj swoje miasto do stworzenia większej liczby cichych miejsc publicznych.",
    "Jaki nawyk poprawił twoją codzienność i dlaczego się u ciebie sprawdza?",
    "Jaka mała decyzja miała większy wpływ, niż się spodziewałeś lub spodziewałaś?",
    "Czy podczas posiłku z innymi telefony powinny zostać poza stołem? Dlaczego?",
    "Czy społeczeństwo bez gotówki jest wygodne, czy ryzykowne? Podaj najmocniejszy argument za swoim zdaniem.",
    "Opowiedz o planie, który zmienił się w ostatniej chwili.",
    "Opowiedz o czymś, co zgubiłeś lub zgubiłaś, i o tym, jak skończyła się ta sytuacja.",
    "Wyjaśnij zupełnie początkującej osobie, jak zacząć hobby, które dobrze znasz.",
    "Opisz swoją okolicę komuś, kto rozważa przeprowadzkę w to miejsce.",
    "Przekonaj kogoś, by wybrał lokalny sklep zamiast dużej sieci.",
    "Przekonaj ludzi, by naprawiali konkretny rodzaj przedmiotu zamiast go wymieniać.",
    "Do jakiej rady wciąż wracasz i dlaczego zapadła ci w pamięć?",
    "Jaki zwyczajny przedmiot ma dla ciebie wartość większą, niż wskazuje jego cena?",
    "Opisz swój zwykły poranek, w tym część, którą najbardziej chcesz zmienić.",
    "Opisz potrawę, która daje ci poczucie komfortu, i wyjaśnij, dlaczego jest wyjątkowa.",
    "Opisz swoją codzienną trasę i to, co zwykle zauważasz po drodze.",
    "Opisz swój ulubiony pokój i trzy szczegóły, które sprawiają, że go lubisz.",
    "Jak odpowiesz na informację zwrotną, z którą się nie zgadzasz?",
    "Jak ustalasz kolejność działań, gdy dwa ważne zadania wymagają uwagi w tym samym czasie?",
    "Wyjaśnij swoją rolę w pracy lub nauce komuś, kto nic o niej nie wie.",
    "Jak powiesz komuś, że nie uda się dotrzymać ważnego terminu?",
    "Wyobraź sobie, że każda doba ma dodatkową godzinę. Jak ją wykorzystasz i co się zmieni?",
    "Gdyby można było zamienić się z kimś codziennym życiem na tydzień, kogo byś wybrał lub wybrała i dlaczego?",
    "Wyobraź sobie weekend bez telefonów i internetu. Co byłoby łatwiejsze, a co trudniejsze?",
    "Wyobraź sobie, że jutro możesz otworzyć małą firmę. Co by oferowała i kto by ją odwiedzał?",
  ],
  ru: [
    "Должен ли каждую неделю быть один день без совещаний? Объясните свою точку зрения.",
    "Должно ли в центрах городов быть меньше автомобилей? Объясните, кто от этого выиграет и что может стать сложнее.",
    "Расскажите о небольшой ошибке, которая помогла вам узнать что-то полезное.",
    "Расскажите о случае, когда незнакомый человек изменил ваш день.",
    "Объясните одну особенность вашей культуры, которую гости часто понимают неправильно.",
    "Объясните, как сделать так, чтобы гость почувствовал себя желанным в вашем доме.",
    "Убедите друга попробовать занятие, которое вам нравится.",
    "Убедите власти вашего города создать больше тихих общественных мест.",
    "Какая привычка улучшила вашу повседневную жизнь и почему она вам помогает?",
    "Какое небольшое решение повлияло на вашу жизнь сильнее, чем вы ожидали?",
    "Следует ли убирать телефоны со стола во время еды с другими людьми? Почему?",
    "Общество без наличных денег — это удобно или рискованно? Приведите самый сильный аргумент в пользу своей позиции.",
    "Расскажите о плане, который изменился в последнюю минуту.",
    "Расскажите о вещи, которую вы потеряли, и о том, чем закончилась эта ситуация.",
    "Объясните новичку, как начать заниматься хобби, которое вы хорошо знаете.",
    "Опишите свой район человеку, который думает туда переехать.",
    "Убедите кого-нибудь выбрать местный магазин вместо крупной сети.",
    "Убедите людей ремонтировать определённый вид вещей, а не заменять их новыми.",
    "Какой совет вы до сих пор вспоминаете и почему он вам запомнился?",
    "Какая обычная вещь значит для вас гораздо больше, чем можно судить по её цене?",
    "Опишите своё обычное утро и назовите часть, которую вы больше всего хотели бы изменить.",
    "Опишите блюдо, которое приносит вам чувство комфорта, и объясните, почему оно для вас особенное.",
    "Опишите свой обычный маршрут и то, что вы обычно замечаете по дороге.",
    "Опишите свою любимую комнату и три детали, благодаря которым она вам нравится.",
    "Как вы ответите на обратную связь, с которой не согласны?",
    "Как вы расставляете приоритеты, если два важных дела требуют внимания одновременно?",
    "Объясните свою роль на работе или в учёбе человеку, который ничего о ней не знает.",
    "Как вы сообщите человеку, что важный срок не будет соблюдён?",
    "Представьте, что в каждом дне появился дополнительный час. Как вы его используете и что изменится?",
    "Если бы вы могли на неделю поменяться с кем-то повседневной жизнью, кого бы вы выбрали и почему?",
    "Представьте выходные без телефонов и интернета. Что было бы проще, а что сложнее?",
    "Представьте, что завтра вы можете открыть небольшой бизнес. Что он будет предлагать и кто туда придёт?",
  ],
  sv: [
    "Borde det finnas en mötesfri dag varje vecka? Förklara vad du tycker.",
    "Borde det finnas färre bilar i stadskärnor? Förklara vem som skulle gynnas och vad som kan bli svårare.",
    "Berätta om ett litet misstag som lärde dig något användbart.",
    "Berätta om en gång då en främling förändrade din dag.",
    "Förklara en del av din kultur som besökare ofta missförstår.",
    "Förklara hur man får en gäst att känna sig välkommen hemma.",
    "Övertyga en vän om att prova en aktivitet som du tycker om.",
    "Övertyga din stad om att skapa fler lugna offentliga platser.",
    "Vilken vana har förbättrat din vardag, och varför fungerar den för dig?",
    "Vilket litet beslut fick större följder än du hade väntat dig?",
    "Borde telefoner hållas borta från bordet när man äter med andra? Varför eller varför inte?",
    "Är ett kontantlöst samhälle bekvämt eller riskabelt? Ge det starkaste argumentet för din åsikt.",
    "Berätta om en plan som ändrades i sista minuten.",
    "Berätta om något du tappade bort och hur situationen slutade.",
    "Förklara hur en fullständig nybörjare kan börja med en hobby som du känner väl.",
    "Beskriv ditt bostadsområde för någon som funderar på att flytta dit.",
    "Övertyga någon om att välja en lokal butik framför en stor kedja.",
    "Övertyga människor om att laga en viss typ av sak i stället för att ersätta den.",
    "Vilket råd tänker du fortfarande på, och varför har det stannat kvar hos dig?",
    "Vilket vanligt föremål betyder mer för dig än vad priset antyder?",
    "Beskriv din vanliga morgon och den del du helst skulle vilja förändra.",
    "Beskriv en maträtt som får dig att känna dig trygg och förklara varför den är speciell.",
    "Beskriv en resa du gör ofta och vad du brukar lägga märke till längs vägen.",
    "Beskriv ditt favoritrum och tre detaljer som gör att du tycker om det.",
    "Hur skulle du svara på återkoppling som du inte håller med om?",
    "Hur prioriterar du när två viktiga uppgifter behöver din uppmärksamhet samtidigt?",
    "Förklara din roll på jobbet eller i studierna för någon som inte vet något om den.",
    "Hur skulle du berätta för någon att en viktig deadline inte kommer att hållas?",
    "Föreställ dig att varje dag hade en extra timme. Hur skulle du använda den, och vad skulle förändras?",
    "Om du kunde byta vardag med någon i en vecka, vem skulle du välja och varför?",
    "Föreställ dig en helg utan telefoner eller internet. Vad skulle vara lättare och vad skulle vara svårare?",
    "Föreställ dig att du kunde starta ett litet företag i morgon. Vad skulle det erbjuda och vilka skulle komma dit?",
  ],
};
